import { makeAutoObservable } from 'mobx';
import { ICreativesStore } from './ICreativesStore';
import { Creative } from 'domains/creative';
import { Paginated } from 'infrastructure/pagination/Paginated';
import { ICreativesService } from 'application/services/creatives/ICreativesService';
import { PaginatedQueryDTO } from 'infrastructure/dtos/pagination';

export class CreativesStore implements ICreativesStore {
  private _creatives: Paginated<Creative> | undefined = undefined;
  private _isLoading: boolean = false;
  private _filterStringDates: string[] = [];
  private _filteredCreatives: Creative[] | undefined = undefined;

  constructor(private readonly service: ICreativesService) {
    makeAutoObservable(this);
  }

  set creatives(value: Paginated<Creative> | undefined) {
    this._creatives = value;
  }

  get creatives(): Paginated<Creative> | undefined {
    return this._creatives;
  }

  set isLoading(value: boolean) {
    this._isLoading = value;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  set filterStringDates(value: string[]) {
    this._filterStringDates = value;
  }

  get filterStringDates(): string[] {
    return this._filterStringDates;
  }

  set filteredCreatives(value: Creative[] | undefined) {
    this._filteredCreatives = value;
  }

  get filteredCreatives(): Creative[] | undefined {
    return this._filteredCreatives;
  }

  private async fetchCreatives(query: PaginatedQueryDTO): Promise<void> {
    this.isLoading = true;
    try {
      this.creatives = await this.service.getPaginatedCreativesWithPostRequest(query);
      this.filterStringDates = this.creatives?.items.map(creative => creative.timestamp) ?? [];
    } finally {
      this.isLoading = false;
    }
  }

  async search(query: string): Promise<void> {
    this.filteredCreatives = this.creatives?.items.filter(creative =>
      creative.username.toLowerCase().includes(query.toLowerCase())
    );
  }

  async filterByDate(date: string): Promise<void> {
    this.filteredCreatives = this.creatives?.items.filter(creative => creative.timestamp === date);
  }

  async getNextPage(): Promise<void> {
    if (!this.creatives) {
      await this.getFirstPage();
      return;
    }

    if (this.creatives.currentPage >= this.creatives.lastPage) {
      return;
    }

    await this.fetchCreatives({ page: this.creatives.currentPage + 1 });
  }

  async getPreviousPage(): Promise<void> {
    if (!this.creatives) {
      await this.getFirstPage();
      return;
    }

    if (this.creatives.currentPage <= 1) {
      return;
    }

    await this.fetchCreatives({ page: this.creatives.currentPage - 1 });
  }

  async getPage(page: number): Promise<void> {
    await this.fetchCreatives({ page });
  }

  async goLastPage(): Promise<void> {
    if (!this.creatives) {
      await this.getFirstPage();
      return;
    }

    await this.fetchCreatives({ page: this.creatives.lastPage });
  }

  async getFirstPage(): Promise<void> {
    await this.fetchCreatives({ page: 1 });
  }
}
